import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { authAPI, productAPI, orderAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  FiGrid, FiPackage, FiShoppingBag, FiSettings,
  FiTrendingUp, FiClock, FiTruck, FiCheck,
  FiEdit2, FiTrash2, FiPlus, FiX, FiEye,
  FiRefreshCw, FiChevronDown, FiUpload, FiImage,
  FiStar, FiDollarSign, FiTag, FiLayers,
  FiUser, FiChevronUp, FiMove, FiMenu,
} from 'react-icons/fi';
import './AdminDashboard.css';

const STATUS_COLORS = {
  pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
  shipped: '#06b6d4', out_for_delivery: '#f97316', delivered: '#22c55e',
  cancelled: '#ef4444', returned: '#6b7280',
};
const ORDER_STATUSES = [
  'pending','confirmed','processing','shipped','out_for_delivery','delivered','cancelled','returned'
];

/* ─── Sidebar ──────────────────────────────────────────────── */
const Sidebar = ({ activeSection, setActiveSection }) => {
  const nav = [
    { key: 'dashboard',       label: 'Dashboard',         icon: <FiGrid size={18}/> },
    { key: 'orders',          label: 'Orders',             icon: <FiShoppingBag size={18}/> },
    { key: 'customer-orders', label: 'Customer History',   icon: <FiUser size={18}/> },
    { key: 'products',        label: 'Products',           icon: <FiPackage size={18}/> },
    { key: 'variants',        label: 'Variants & Pricing', icon: <FiSettings size={18}/> },
    { key: 'manual-order',    label: 'Place Order',        icon: <FiPlus size={18}/> },
  ];
  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar__brand">
        <Link to="/" className="admin-sidebar__logo">Vedonica International</Link>
        <span className="admin-sidebar__badge">Admin</span>
      </div>
      <nav className="admin-sidebar__nav">
        {nav.map(item => (
          <button key={item.key}
            className={`admin-nav-item ${activeSection === item.key ? 'admin-nav-item--active' : ''}`}
            onClick={() => setActiveSection(item.key)}>
            {item.icon}<span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="admin-sidebar__footer">
        <Link to="/" className="admin-sidebar__back">← Back to Store</Link>
      </div>
    </aside>
  );
};

/* ─── StatCard ─────────────────────────────────────────────── */
const StatCard = ({ label, value, icon, color, sub }) => (
  <div className="stat-card">
    <div className="stat-card__icon" style={{ background: color + '18', color }}>{icon}</div>
    <div className="stat-card__info">
      <p className="stat-card__value">{value}</p>
      <p className="stat-card__label">{label}</p>
      {sub && <p className="stat-card__sub">{sub}</p>}
    </div>
  </div>
);

/* ─── Order Detail Panel ───────────────────────────────────── */
const OrderDetailPanel = ({ selectedOrder, onClose, onUpdated }) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: '', note: '', tracking_number: '', payment_status: ''
  });

 useEffect(() => {
  if (selectedOrder) {
    setStatusForm({
      status: selectedOrder.status,
      note: '',
      tracking_number:
        selectedOrder.tracking_number ||
        selectedOrder.awb_code ||
        '',
      payment_status: selectedOrder.payment_status,
    });
  }
}, [selectedOrder]);

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    setUpdatingStatus(true);
    try {
      await orderAPI.adminUpdateStatus(selectedOrder.order_id, statusForm);
      toast.success('Order updated!');
      if (onUpdated) onUpdated();
    } catch { toast.error('Failed to update order'); }
    finally { setUpdatingStatus(false); }
  };

  if (!selectedOrder) {
    return (
      <div className="admin-order-panel admin-order-panel--empty">
        <FiEye size={40} style={{ color: 'var(--gray-300)', marginBottom: 16 }} />
        <p>Select an order to view details</p>
      </div>
    );
  }

  return (
    <div className="admin-order-panel">
      <div className="admin-order-panel__header">
        <h3>Order #{selectedOrder.order_id}</h3>
        {onClose && (
          <button className="admin-close-btn" onClick={onClose} aria-label="Close order panel">
            <FiX size={18} />
          </button>
        )}
      </div>

      {/* Customer */}
      <div className="admin-order-panel__section">
        <p className="admin-panel-label">Customer</p>
        <p style={{ fontWeight: 600 }}>{selectedOrder.shipping_name}</p>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{selectedOrder.shipping_phone}</p>
        {selectedOrder.user && (
          <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>{selectedOrder.user}</p>
        )}
      </div>

      {/* Shipping Address */}
      <div className="admin-order-panel__section">
        <p className="admin-panel-label">Shipping Address</p>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {selectedOrder.shipping_address_line1}
          {selectedOrder.shipping_address_line2 && ', ' + selectedOrder.shipping_address_line2}<br />
          {selectedOrder.shipping_city}, {selectedOrder.shipping_state} – {selectedOrder.shipping_pincode}
        </p>
      </div>

      {/* Items */}
      <div className="admin-order-panel__section">
        <p className="admin-panel-label">Items</p>
        {selectedOrder.items?.map(item => (
          <div key={item.id} className="admin-order-item">
            <span>{item.product_name} (Pack {item.pack_size}) ×{item.quantity}</span>
            <span style={{ fontWeight: 600 }}>₹{Number(item.subtotal).toFixed(0)}</span>
          </div>
        ))}
        {Number(selectedOrder.discount_amount) > 0 && (
          <div className="admin-order-item" style={{ color: '#16a34a' }}>
            <span>Discount</span>
            <span>− ₹{Number(selectedOrder.discount_amount).toFixed(2)}</span>
          </div>
        )}
        {Number(selectedOrder.shipping_charge) > 0 && (
          <div className="admin-order-item">
            <span>Shipping</span>
            <span>₹{Number(selectedOrder.shipping_charge).toFixed(2)}</span>
          </div>
        )}
        <div className="admin-order-item admin-order-item--total">
          <span>Total</span>
          <span>₹{Number(selectedOrder.total_amount).toFixed(2)}</span>
        </div>
        {selectedOrder.coupon_code && (
          <p style={{ fontSize: '0.75rem', color: '#2563eb', marginTop: 6 }}>
            🏷 Coupon: <strong>{selectedOrder.coupon_code}</strong>
          </p>
        )}
      </div>

      {/* Tracking & Shiprocket Info */}
      {(selectedOrder.awb_code || selectedOrder.courier_name || selectedOrder.shiprocket_order_id) && (
        <div className="admin-order-panel__section">
          <p className="admin-panel-label">Shipping & Tracking</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.82rem' }}>
            {selectedOrder.courier_name && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Courier</span>
                <span style={{ fontWeight: 600 }}>{selectedOrder.courier_name}</span>
              </div>
            )}
            {selectedOrder.awb_code && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)' }}>AWB Code</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--green-primary)', wordBreak: 'break-all' }}>
                  {selectedOrder.awb_code}
                </span>
              </div>
            )}
            {selectedOrder.shipping_status && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Shipping Status</span>
                <span style={{ fontWeight: 600 }}>{selectedOrder.shipping_status}</span>
              </div>
            )}
            {selectedOrder.shiprocket_order_id && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Shiprocket Order ID</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{selectedOrder.shiprocket_order_id}</span>
              </div>
            )}
            {selectedOrder.shiprocket_shipment_id && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Shipment ID</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{selectedOrder.shiprocket_shipment_id}</span>
              </div>
            )}
            {selectedOrder.estimated_delivery && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Est. Delivery</span>
                <span style={{ fontWeight: 600 }}>
                  {new Date(selectedOrder.estimated_delivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>
          {selectedOrder?.tracking_data?.tracking_data
 ?.shipment_track_activities?.length > 0 && (

<div style={{ marginTop: '15px' }}>

  <p
    style={{
      fontWeight: 600,
      marginBottom: '10px'
    }}
  >
    Tracking Timeline
  </p>

  {selectedOrder
    .tracking_data
    .tracking_data
    .shipment_track_activities
    .map((item,index) => (

    <div
      key={index}
      style={{
        borderLeft:'3px solid #22c55e',
        paddingLeft:'12px',
        marginBottom:'12px'
      }}
    >
      <strong>
        {item.activity}
      </strong>

      <div
        style={{
          fontSize:'12px',
          color:'#666'
        }}
      >
        {item.location}
      </div>

      <div
        style={{
          fontSize:'12px',
          color:'#999'
        }}
      >
        {item.date}
      </div>

    </div>

  ))}

</div>

)}
          {selectedOrder.customer_note && (
            <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}>
              <p style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, marginBottom: 2 }}>Customer Note</p>
              <p style={{ fontSize: '0.8rem', color: '#78350f' }}>{selectedOrder.customer_note}</p>
            </div>
          )}
        </div>
      )}

      {/* Update Status */}
      <div className="admin-order-panel__section">
        <p className="admin-panel-label">Update Status</p>
        <div className="form-group">
          <select className="form-input form-select" value={statusForm.status}
            onChange={e => setStatusForm(f => ({ ...f, status: e.target.value }))}>
            {ORDER_STATUSES.map(s => (
              <option key={s} value={s}>
                {s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Payment Status</label>
          <select className="form-input form-select" value={statusForm.payment_status}
            onChange={e => setStatusForm(f => ({ ...f, payment_status: e.target.value }))}>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Tracking Number</label>
          <input className="form-input" value={statusForm.tracking_number}
            onChange={e => setStatusForm(f => ({ ...f, tracking_number: e.target.value }))}
            placeholder="e.g. DHL1234567890" />
        </div>
        <div className="form-group">
          <label className="form-label">Note (optional)</label>
          <textarea className="form-input" rows={3} value={statusForm.note}
            onChange={e => setStatusForm(f => ({ ...f, note: e.target.value }))}
            placeholder="Internal note..." />
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }}
          onClick={handleUpdateStatus} disabled={updatingStatus}>
          {updatingStatus ? 'Updating...' : 'Update Order'}
        </button>
      </div>

      {/* Status History */}
      {selectedOrder.tracking_events?.length > 0 && (
  <div className="admin-order-panel__section">
    <p className="admin-panel-label">
      Tracking Timeline
    </p>

    {selectedOrder.tracking_events.map(event => (
      <div
        key={event.id}
        className="tracking-event"
      >
        <div className="tracking-dot"></div>

        <div>
          <strong>{event.status}</strong>

          <p>
            {new Date(
              event.event_time
            ).toLocaleString()}
          </p>
        </div>
      </div>
    ))}
  </div>
)}
    </div>
  );
};

/* ─── Customer Order History ───────────────────────────────── */
const CustomerOrderHistory = () => {
  const [phone, setPhone] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    authAPI.getCustomers()
      .then(res => setCustomers(res.data || []))
      .catch(() => {});
  }, []);

  const searchByPhone = () => {
    if (!phone.trim()) return;
    const clean = n => n.replace(/\D/g, '').slice(-10);
    const found = customers.filter(u => clean(u.phone || '').includes(clean(phone)));
    if (found.length === 0) { toast.error('No customer found with this phone number'); return; }
    loadOrders(found[0]);
  };

  const loadOrders = async (customer) => {
    setSelectedCustomer(customer);
    setSelectedOrder(null);
    setLoading(true);
    try {
      const res = await orderAPI.adminList({ phone: customer.phone });
      const d = res.data;
      setOrders(Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []));
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const openOrder = async (order) => {
    try {
      const res = await orderAPI.adminGet(
  order.order_id
);



setSelectedOrder({
  ...res.data,
  tracking_data:
    trackingRes.data.tracking_data
});
    } catch { toast.error('Could not load order details'); }
  };

const refreshSelected = async () => {
  if (selectedOrder) {

    const res =
      await orderAPI.adminGet(selectedOrder.order_id);

    const trackingRes =
      await orderAPI.track(selectedOrder.order_id);

    setSelectedOrder({
      ...res.data,
      tracking_data:
        trackingRes.data.tracking_data
    });

  }

  if (selectedCustomer)
    loadOrders(selectedCustomer);
};

  const totalSpent = orders.reduce((s, o) => s + Number(o.total_amount), 0);
  const deliveredCnt = orders.filter(o => o.status === 'delivered').length;

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <div>
          <h1 className="admin-section__title">Customer Order History</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 4 }}>
            Look up all orders placed by a specific customer.
          </p>
        </div>
      </div>

      {/* Search bar */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
        padding: 20, marginBottom: 24,
      }}>
        <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 10 }}>
          Search by Phone or Select Customer
        </p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="form-input" type="tel" placeholder="Enter phone number"
            value={phone} onChange={e => setPhone(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchByPhone()}
            style={{ flex: '1 1 200px', minWidth: 0 }} />
          <button className="btn btn-primary" onClick={searchByPhone} disabled={searchLoading}
            style={{ whiteSpace: 'nowrap' }}>
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        <select className="form-input form-select"
          onChange={e => {
            const cust = customers.find(c => String(c.id) === e.target.value);
            if (cust) { setPhone(cust.phone || ''); loadOrders(cust); }
          }}
          defaultValue="">
          <option value="">— Or pick from existing customers —</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>
          ))}
        </select>
      </div>

      {selectedCustomer && (
        <>
          {/* Customer summary card */}
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12,
            padding: '16px 20px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: '#2e7d32', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.1rem', fontWeight: 700, flexShrink: 0,
            }}>
              {(selectedCustomer.name || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <p style={{ fontWeight: 700, color: '#166534', fontSize: '1rem' }}>{selectedCustomer.name}</p>
              <p style={{ fontSize: '0.82rem', color: '#16a34a' }}>
                {selectedCustomer.phone}
                {selectedCustomer.email && ` · ${selectedCustomer.email}`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                { val: orders.length, label: 'Total Orders' },
                { val: `₹${totalSpent.toLocaleString('en-IN')}`, label: 'Lifetime Value' },
                { val: deliveredCnt, label: 'Delivered' },
              ].map(({ val, label }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, fontSize: '1.2rem', color: '#166534' }}>{val}</p>
                  <p style={{ fontSize: '0.72rem', color: '#16a34a' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="orders-management-layout">
            <div className="admin-orders-list">
              {loading
                ? <div className="loading-container"><div className="spinner" /></div>
                : orders.length === 0
                  ? <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                    <p style={{ fontSize: '2rem', marginBottom: 12 }}>📦</p>
                    <p>No orders found for this customer</p>
                  </div>
                  : orders.map(order => (
                    <div key={order.id}
                      className={`admin-order-row ${selectedOrder?.id === order.id ? 'admin-order-row--selected' : ''}`}
                      onClick={() => openOrder(order)}>
                      <div className="admin-order-row__main">
                        <span className="order-id-tag">#{order.order_id}</span>
                        <span className="status-badge" style={{
                          background: (STATUS_COLORS[order.status] || '#6b7280') + '22',
                          color: STATUS_COLORS[order.status] || '#6b7280'
                        }}>{order.status_display}</span>
                      </div>
                      <p className="admin-order-row__name" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {order.items?.map(i => `${i.product_name} ×${i.quantity}`).join(', ') ||
                          `${order.items_count || ''} item(s)`}
                      </p>
                      <div className="admin-order-row__meta">
                        <span style={{ fontWeight: 700, color: 'var(--green-primary)' }}>
                          ₹{Number(order.total_amount).toFixed(0)}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))
              }
            </div>
            <OrderDetailPanel
              selectedOrder={selectedOrder}
              onClose={() => setSelectedOrder(null)}
              onUpdated={refreshSelected}
            />
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Manual Order Placement ───────────────────────────────── */
const ADMIN_PAYMENT_OPTIONS = [
  { value: 'razorpay', label: 'Razorpay / Card / UPI', icon: '💳', desc: 'All cards, UPI, net banking via Razorpay', prepaid: true },
  { value: 'upi',      label: 'UPI',                   icon: '📱', desc: 'GPay, PhonePe, Paytm & more',             prepaid: true },
  { value: 'cod',      label: 'Cash on Delivery',       icon: '💵', desc: 'Pay when order arrives',                  prepaid: false },
];

const ManualOrderPlacement = () => {
  const [allVariants, setAllVariants]     = useState([]);
  const [userPhone, setUserPhone]         = useState('');
  const [userEmail, setUserEmail]         = useState('');
  const [items, setItems]                 = useState([{ variant_id: '', quantity: 1 }]);
  const [shipping, setShipping]           = useState({
    shipping_name: '', shipping_phone: '', shipping_address_line1: '',
    shipping_address_line2: '', shipping_city: '', shipping_state: '', shipping_pincode: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [customerNote, setCustomerNote]   = useState('');
  const [placing, setPlacing]             = useState(false);
  const [placedOrder, setPlacedOrder]     = useState(null);
  const [existingUsers, setExistingUsers] = useState([]);
  const [selectedUser, setSelectedUser]   = useState('');
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [promoCode, setPromoCode]         = useState('');
  const [promoData, setPromoData]         = useState(null);
  const [promoLoading, setPromoLoading]   = useState(false);

  useEffect(() => {
    productAPI.getAdminAll().then(res => {
      const prods = Array.isArray(res.data) ? res.data : (res.data.results || []);
      const variants = [];
      prods.forEach(p => {
        (p.variants || []).forEach(v => {
          variants.push({
            id: v.id,
            label: `${p.name} — Pack of ${v.pack_size} — ₹${Number(v.effective_price).toFixed(0)}`,
            stock: v.stock,
            price: Number(v.effective_price),
          });
        });
      });
      setAllVariants(variants);
    }).catch(() => toast.error('Failed to load products'));
  }, []);

  useEffect(() => {
    authAPI.getCustomers()
      .then(res => setExistingUsers(res.data || []))
      .catch(err => console.log(err));
  }, []);

  const handleSelectExistingUser = (userId) => {
    setSelectedUser(userId);
    if (!userId) return;
    const user = existingUsers.find(u => String(u.id) === String(userId));
    if (user) {
      setIsExistingUser(true);
      setUserPhone(user.phone || '');
      setUserEmail(user.email || '');
      setShipping(prev => ({
        ...prev,
        shipping_name: user.name || '',
        shipping_phone: user.phone || '',
        shipping_address_line1: user.address_line1 || '',
        shipping_address_line2: user.address_line2 || '',
        shipping_city: user.city || '',
        shipping_state: user.state || '',
        shipping_pincode: user.pincode || '',
      }));
      toast.success('Customer loaded!');
    }
  };

  const checkExistingUser = () => {
    if (!userPhone) return;
    const clean = n => n.replace(/\D/g, '').slice(-10);
    const user = existingUsers.find(u => clean(u.phone) === clean(userPhone));
    if (user) {
      setIsExistingUser(true);
      toast.success('Existing customer found');
      setUserEmail(user.email || '');
      setShipping(prev => ({
        ...prev,
        shipping_name: user.name || '',
        shipping_phone: user.phone || '',
        shipping_address_line1: user.address_line1 || '',
        shipping_address_line2: user.address_line2 || '',
        shipping_city: user.city || '',
        shipping_state: user.state || '',
        shipping_pincode: user.pincode || '',
      }));
    } else { setIsExistingUser(false); }
  };

  const isPrepaid = ADMIN_PAYMENT_OPTIONS.find(p => p.value === paymentMethod)?.prepaid ?? false;
  const subtotal = items.reduce((sum, item) => {
    const v = allVariants.find(v => String(v.id) === String(item.variant_id));
    return sum + (v ? v.price * item.quantity : 0);
  }, 0);
  const prepaidDiscount = isPrepaid ? Math.round(subtotal * 0.10) : 0;
  const promoDiscount   = promoData ? Number(promoData.discount_amount) : 0;
  const totalDiscount   = prepaidDiscount + promoDiscount;
  const shippingCharge  = subtotal >= 500 ? 0 : 90;
  const grandTotal      = subtotal - totalDiscount + shippingCharge;

  const addItem    = () => setItems(p => [...p, { variant_id: '', quantity: 1 }]);
  const removeItem = idx => setItems(p => p.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) =>
    setItems(p => p.map((item, i) => i === idx ? { ...item, [field]: val } : item));

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await productAPI.validateCoupon({ code: promoCode.trim(), order_amount: subtotal });
      const data = res.data;
      if (data && data.discount_amount) data.discount_amount = Number(data.discount_amount);
      setPromoData(data);
      toast.success(`Promo applied! Saving ₹${Number(data.discount_amount).toFixed(0)}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid promo code');
      setPromoData(null);
    } finally { setPromoLoading(false); }
  };

  const resetForm = () => {
    setUserPhone(''); setUserEmail(''); setSelectedUser(''); setIsExistingUser(false);
    setItems([{ variant_id: '', quantity: 1 }]);
    setShipping({ shipping_name: '', shipping_phone: '', shipping_address_line1: '',
      shipping_address_line2: '', shipping_city: '', shipping_state: '', shipping_pincode: '' });
    setCustomerNote('');
    setPaymentMethod('upi'); setPaymentStatus('pending');
    setPromoCode(''); setPromoData(null);
  };

  const openRazorpay = (rzpOrderId, rzpKey, finalAmount) => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) { toast.error('Razorpay SDK failed to load'); reject(new Error('Razorpay unavailable')); return; }
      const options = {
        key: rzpKey, amount: Math.round(finalAmount * 100), currency: 'INR',
        name: 'Vedonica International', description: `Manual order for ${userPhone}`,
        order_id: rzpOrderId,
        prefill: { contact: shipping.shipping_phone || userPhone, email: userEmail || '', name: shipping.shipping_name || '' },
        theme: { color: '#2e7d32' },
        handler: (response) => resolve(response),
        modal: { ondismiss: () => reject(new Error('Payment cancelled.')) },
      };
      new window.Razorpay(options).open();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userPhone.trim()) return toast.error('Customer phone number is required.');
    if (items.some(i => !i.variant_id)) return toast.error('Please select a product variant for all items.');
    setPlacing(true); setPlacedOrder(null);
    try {
      let paymentDetails = {};
      if (paymentMethod === 'razorpay' || paymentMethod === 'upi') {
        let rzpOrder;
        try {
          const rzpRes = await orderAPI.createRazorpayOrder({ amount: Number(grandTotal) });
          rzpOrder = rzpRes.data;
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to initiate Razorpay.');
          setPlacing(false); return;
        }
        let paymentResponse;
        try {
          paymentResponse = await openRazorpay(rzpOrder.razorpay_order_id, rzpOrder.key, Number(grandTotal));
        } catch (err) {
          toast.error(err.message || 'Payment was not completed.');
          setPlacing(false); return;
        }
        paymentDetails = {
          razorpay_order_id:   paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature:  paymentResponse.razorpay_signature,
        };
      }
      const res = await orderAPI.adminPlaceOrder({
        user_phone: userPhone.trim(),
        email: userEmail.trim(),
        items: items.map(i => ({ variant_id: i.variant_id, quantity: i.quantity })),
        ...shipping,
        payment_method: isPrepaid ? 'razorpay' : 'cod',
        payment_status: isPrepaid ? 'paid' : paymentStatus,
        coupon_code: promoData ? promoCode : '',
        customer_note: customerNote,
        ...paymentDetails,
      });
      setPlacedOrder(res.data);
      toast.success(`Order ${res.data.order_id} placed successfully!`);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to place order. Please try again.');
    } finally { setPlacing(false); }
  };

  const sectionStyle = {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 12, padding: '20px', marginBottom: 20,
  };
  const labelStyle = {
    display: 'block', fontSize: '0.82rem', fontWeight: 600,
    color: '#374151', marginBottom: 6,
  };
  const stepBadge = (n) => (
    <span style={{
      background: '#e8f5e9', color: '#2e7d32', borderRadius: '50%',
      width: 24, height: 24, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0,
    }}>{n}</span>
  );
  const sectionTitle = (n, title) => (
    <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: '0.95rem',
      color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
      {stepBadge(n)}{title}
    </h3>
  );

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <div>
          <h1 className="admin-section__title">Place Order Manually</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 4 }}>
            Create an order on behalf of a customer — no OTP or login required from the customer's side.
          </p>
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Select Existing Customer</label>
        <select className="form-input form-select" value={selectedUser}
          onChange={e => handleSelectExistingUser(e.target.value)}>
          <option value="">-- Select Customer --</option>
          {existingUsers.map(user => (
            <option key={user.id} value={user.id}>{user.name} — {user.phone}</option>
          ))}
        </select>
      </div>

      {placedOrder && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12,
          padding: '16px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12
        }}>
          <div>
            <p style={{ fontWeight: 700, color: '#15803d', fontSize: '1rem' }}>✓ Order Placed Successfully</p>
            <p style={{ color: '#166534', fontSize: '0.85rem', marginTop: 2 }}>
              Order ID: <strong>{placedOrder.order_id}</strong>
              &nbsp;·&nbsp;Amount: <strong>₹{Number(placedOrder.total_amount).toFixed(2)}</strong>
              &nbsp;·&nbsp;Payment: <strong style={{ textTransform: 'capitalize' }}>{placedOrder.payment_status}</strong>
            </p>
          </div>
          <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => setPlacedOrder(null)}>Dismiss</button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Step 1: Customer Info */}
        <div style={sectionStyle}>
          {sectionTitle(1, 'Customer Information')}
          <div className="admin-form-grid">
            <div className="form-group">
              <label style={labelStyle}>Phone Number *</label>
              <input className="form-input" type="tel" maxLength={10}
                placeholder="10-digit mobile number"
                value={userPhone}
                onChange={e => setUserPhone(e.target.value)}
                onBlur={checkExistingUser} required />
              {isExistingUser && (
                <p style={{ color: '#16a34a', fontSize: '0.78rem', marginTop: 5, fontWeight: 600 }}>
                  ✓ Existing customer found
                </p>
              )}
            </div>
            <div className="form-group">
              <label style={labelStyle}>Email Address</label>
              <input className="form-input" type="email"
                placeholder="customer@example.com (optional)"
                value={userEmail} onChange={e => setUserEmail(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Step 2: Items */}
        <div style={sectionStyle}>
          {sectionTitle(2, 'Order Items')}
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '3 1 180px', minWidth: 0 }}>
                {idx === 0 && <label style={labelStyle}>Product Variant *</label>}
                <select className="form-input form-select" value={item.variant_id} required
                  onChange={e => updateItem(idx, 'variant_id', e.target.value)}>
                  <option value="">— Select product variant —</option>
                  {allVariants.map(v => (
                    <option key={v.id} value={v.id} disabled={v.stock === 0}>
                      {v.label}{v.stock === 0 ? ' — Out of Stock' : ` (Stock: ${v.stock})`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 70px', minWidth: 70 }}>
                {idx === 0 && <label style={labelStyle}>Qty *</label>}
                <input className="form-input" type="number" min={1} value={item.quantity} required
                  onChange={e => updateItem(idx, 'quantity', Number(e.target.value))} />
              </div>
              <div style={{ paddingBottom: 0 }}>
                {idx === 0 && <div style={{ height: 26 }} />}
                <button type="button"
                  style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 8,
                    padding: '9px 10px', cursor: 'pointer', color: '#ef4444',
                    opacity: items.length === 1 ? 0.3 : 1 }}
                  disabled={items.length === 1} onClick={() => removeItem(idx)}>
                  <FiX size={15} />
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-ghost" onClick={addItem}
            style={{ fontSize: '0.85rem', marginTop: 4 }}>
            <FiPlus size={14} /> Add Another Item
          </button>
        </div>

        {/* Step 3: Shipping */}
        <div style={sectionStyle}>
          {sectionTitle(3, 'Shipping Address')}
          <div className="admin-form-grid">
            {[
              ['shipping_name','Full Name *',true,'text'],
              ['shipping_phone','Contact Number *',true,'tel'],
              ['shipping_address_line1','Address Line 1 *',true,'text'],
              ['shipping_address_line2','Address Line 2',false,'text'],
              ['shipping_city','City *',true,'text'],
              ['shipping_state','State *',true,'text'],
              ['shipping_pincode','Pincode *',true,'text'],
            ].map(([field, label, req, type]) => (
              <div className="form-group" key={field}>
                <label style={labelStyle}>{label}</label>
                <input className="form-input" type={type} required={req}
                  value={shipping[field]}
                  onChange={e => setShipping(s => ({ ...s, [field]: e.target.value }))} />
              </div>
            ))}
          </div>
        </div>

        {/* Step 4: Payment */}
        <div style={sectionStyle}>
          {sectionTitle(4, 'Payment Method')}
          <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Pay Online</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {ADMIN_PAYMENT_OPTIONS.filter(pm => pm.prepaid).map(pm => (
              <div key={pm.value} onClick={() => setPaymentMethod(pm.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  border: `1.5px solid ${paymentMethod === pm.value ? '#2e7d32' : '#e5e7eb'}`,
                  borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                  background: paymentMethod === pm.value ? '#f0fdf4' : '#fff', transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{pm.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{pm.label}</p>
                    <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.68rem',
                      fontWeight: 700, padding: '1px 8px', borderRadius: 20 }}>10% OFF</span>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '2px 0 0' }}>{pm.desc}</p>
                  {paymentMethod === pm.value && isPrepaid && subtotal > 0 && (
                    <p style={{ fontSize: '0.78rem', color: '#16a34a', margin: '4px 0 0', fontWeight: 600 }}>
                      ⚡ 10% prepaid discount applied — saving ₹{prepaidDiscount.toFixed(0)}
                    </p>
                  )}
                </div>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${paymentMethod === pm.value ? '#2e7d32' : '#d1d5db'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: paymentMethod === pm.value ? '#2e7d32' : 'transparent',
                }}>
                  {paymentMethod === pm.value && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Other</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ADMIN_PAYMENT_OPTIONS.filter(pm => !pm.prepaid).map(pm => (
              <div key={pm.value} onClick={() => setPaymentMethod(pm.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  border: `1.5px solid ${paymentMethod === pm.value ? '#2e7d32' : '#e5e7eb'}`,
                  borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                  background: paymentMethod === pm.value ? '#f0fdf4' : '#fff', transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{pm.icon}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{pm.label}</p>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '2px 0 0' }}>{pm.desc}</p>
                </div>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${paymentMethod === pm.value ? '#2e7d32' : '#d1d5db'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: paymentMethod === pm.value ? '#2e7d32' : 'transparent',
                }}>
                  {paymentMethod === pm.value && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                </div>
              </div>
            ))}
          </div>

          {paymentMethod === 'cod' && (
            <div className="form-group" style={{ marginTop: 14 }}>
              <label style={labelStyle}>Payment Status</label>
              <select className="form-input form-select" value={paymentStatus}
                onChange={e => setPaymentStatus(e.target.value)}>
                <option value="pending">Pending (collect on delivery)</option>
                <option value="paid">Already Paid</option>
              </select>
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {promoData ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', gap: 8 }}>
                <span style={{ color: '#2563eb', fontSize: '0.85rem' }}>
                  Promo ({promoCode}) — saving ₹{promoDiscount.toFixed(0)}
                </span>
                <button type="button"
                  onClick={() => { setPromoData(null); setPromoCode(''); }}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}>
                  ✕ Remove
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input className="form-input" value={promoCode}
                  placeholder="Extra promo code (optional)"
                  onChange={e => setPromoCode(e.target.value.toUpperCase())}
                  style={{ flex: '1 1 140px', minWidth: 0 }} />
                <button type="button" className="btn btn-ghost"
                  onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()}
                  style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiTag size={14} /> {promoLoading ? 'Applying...' : 'Apply'}
                </button>
              </div>
            )}
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label style={labelStyle}>Customer Note</label>
            <input className="form-input" value={customerNote}
              placeholder="Any special instructions..."
              onChange={e => setCustomerNote(e.target.value)} />
          </div>
        </div>

        {/* Order Summary */}
        {subtotal > 0 && (
          <div style={{ ...sectionStyle, background: '#f9fafb' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: '0.95rem', color: '#111827' }}>Order Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              {prepaidDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#16a34a' }}>Prepaid Discount (10%)</span>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>− ₹{prepaidDiscount.toFixed(2)}</span>
                </div>
              )}
              {promoDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#2563eb' }}>Promo Discount ({promoCode})</span>
                  <span style={{ color: '#2563eb', fontWeight: 600 }}>− ₹{promoDiscount.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Shipping</span>
                <span style={{ color: shippingCharge === 0 ? '#16a34a' : 'inherit', fontWeight: 500 }}>
                  {shippingCharge === 0 ? 'FREE' : `₹${shippingCharge}.00`}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                borderTop: '1px solid #e5e7eb', paddingTop: 10, marginTop: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '1rem' }}>Total Payable</span>
                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--green-primary)' }}>
                  ₹{grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={placing}
          style={{ width: '100%', padding: '14px 20px', fontSize: '1rem',
            borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {placing
            ? <><div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Processing...</>
            : (paymentMethod === 'razorpay' || paymentMethod === 'upi')
              ? <>Proceed to Payment &amp; Place Order</>
              : <><FiCheck size={18} /> Confirm &amp; Place Order</>
          }
        </button>
      </form>
    </div>
  );
};

/* ─── Dashboard Overview ───────────────────────────────────── */
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([orderAPI.adminStats(), orderAPI.adminList({ page_size: 5 })])
      .then(([sRes, oRes]) => {
        setStats(sRes.data);
        const d = oRes.data;
        setRecentOrders(Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []));
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="admin-section">
      <h1 className="admin-section__title">Dashboard</h1>
      <div className="stats-grid">
        <StatCard label="Total Orders"    value={stats?.total_orders || 0}                                                    icon={<FiShoppingBag size={22} />} color="#0f5132" />
        <StatCard label="Pending Orders"  value={stats?.pending_orders || 0}  sub="Needs attention"                           icon={<FiClock size={22} />}       color="#f59e0b" />
        <StatCard label="Shipped"         value={stats?.shipped_orders || 0}                                                  icon={<FiTruck size={22} />}       color="#06b6d4" />
        <StatCard label="Delivered"       value={stats?.delivered_orders || 0}                                                icon={<FiCheck size={22} />}       color="#22c55e" />
        <StatCard label="Today's Orders"  value={stats?.today_orders || 0}                                                    icon={<FiTrendingUp size={22} />}  color="#8b5cf6" />
        <StatCard label="Monthly Revenue" value={`₹${Number(stats?.month_revenue || 0).toLocaleString('en-IN')}`} sub="Paid only" icon={<FiTrendingUp size={22} />} color="#ec4899" />
      </div>
      <div className="recent-orders">
        <div className="recent-orders__header">
          <h2 className="admin-subtitle">Recent Orders</h2>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Amount</th>
                <th className="hide-xs">Payment</th>
                <th>Status</th>
                <th className="hide-xs">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(o => (
                <tr key={o.id}>
                  <td><span className="order-id-tag">#{o.order_id}</span></td>
                  <td>
                    <p style={{ fontWeight: 600, fontSize: '0.875rem' }}>{o.shipping_name}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{o.user}</p>
                  </td>
                  <td style={{ fontWeight: 700, color: 'var(--green-primary)', whiteSpace: 'nowrap' }}>
                    ₹{Number(o.total_amount).toFixed(2)}
                  </td>
                  <td className="hide-xs" style={{ textTransform: 'uppercase', fontSize: '0.78rem', fontWeight: 600 }}>
                    {o.payment_method}
                  </td>
                  <td>
                    <span className="status-badge" style={{
                      background: (STATUS_COLORS[o.status] || '#6b7280') + '22',
                      color: STATUS_COLORS[o.status] || '#6b7280'
                    }}>
                      {o.status_display}
                    </span>
                  </td>
                  <td className="hide-xs" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </td>
                </tr>
              ))}
              {recentOrders.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ─── Orders Management ────────────────────────────────────── */
const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    orderAPI.adminList(statusFilter ? { status: statusFilter } : {})
      .then(res => {
        const d = res.data;
        setOrders(Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []));
      }).catch(() => toast.error('Failed to load orders')).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSelectOrder = async (order) => {

  const res =
    await orderAPI.adminGet(order.order_id);

  const trackingRes =
    await orderAPI.track(order.order_id);

  setSelectedOrder({
    ...res.data,
    tracking_data:
      trackingRes.data.tracking_data
  });

};

  const refreshSelected = async () => {
    fetchOrders();
    if (selectedOrder) {
      const res = await orderAPI.adminGet(selectedOrder.order_id);
      setSelectedOrder(res.data);
    }
  };

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h1 className="admin-section__title">Orders</h1>
        <div className="admin-filters">
          <select className="form-input form-select admin-filter-select" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {ORDER_STATUSES.map(s => (
              <option key={s} value={s}>
                {s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </option>
            ))}
          </select>
          <button className="btn btn-ghost admin-refresh-btn" onClick={fetchOrders} aria-label="Refresh orders">
            <FiRefreshCw size={15} />
          </button>
        </div>
      </div>
      <div className="orders-management-layout">
        <div className="admin-orders-list">
          {loading
            ? <div className="loading-container"><div className="spinner" /></div>
            : orders.length === 0
              ? <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                <p style={{ fontSize: '2rem', marginBottom: 12 }}>📦</p><p>No orders found</p>
              </div>
              : orders.map(order => (
                <div key={order.id}
                  className={`admin-order-row ${selectedOrder?.id === order.id ? 'admin-order-row--selected' : ''}`}
                  onClick={() => handleSelectOrder(order)}>
                  <div className="admin-order-row__main">
                    <span className="order-id-tag">#{order.order_id}</span>
                    <span className="status-badge" style={{
                      background: (STATUS_COLORS[order.status] || '#6b7280') + '22',
                      color: STATUS_COLORS[order.status] || '#6b7280'
                    }}>{order.status_display}</span>
                  </div>
                  <p className="admin-order-row__name">{order.shipping_name}</p>
                  <div className="admin-order-row__meta">
                    <span style={{ fontWeight: 700, color: 'var(--green-primary)' }}>₹{Number(order.total_amount).toFixed(0)}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))
          }
        </div>
        <OrderDetailPanel
          selectedOrder={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdated={refreshSelected}
        />
      </div>
    </div>
  );
};

/* ─── Image Upload Zone ────────────────────────────────────── */
const ImageUploadZone = ({ productId, existingImages, onRefresh }) => {
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const doUpload = async (files) => {
    if (!files || !files.length || !productId) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('images', f));
    formData.append('is_primary', true);
    try {
      await productAPI.uploadImages(productId, formData);
      toast.success(`${files.length} image(s) uploaded!`);
      onRefresh();
    } catch { toast.error('Image upload failed'); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    setDeletingId(imageId);
    try {
      await productAPI.deleteImage(productId, imageId);
      toast.success('Image deleted successfully');
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Failed to delete image');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="image-upload-zone">
      <style>{`
        .image-upload-zone { display: flex; flex-direction: column; gap: 16px; }
        .image-dropzone { border: 2px dashed #a5d6a7; border-radius: 12px; padding: 32px 20px; text-align: center; cursor: pointer; transition: all 0.2s; background: #f9fdf9; }
        .image-dropzone:hover, .image-dropzone--active { border-color: #2e7d32; background: #e8f5e9; }
        .image-gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 10px; }
        .image-gallery-item { position: relative; border-radius: 10px; overflow: hidden; aspect-ratio: 1; background: #f0f0f0; border: 2px solid #e0e0e0; transition: border-color 0.2s; }
        .image-gallery-item:hover { border-color: #2e7d32; }
        .image-gallery-item img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .image-gallery-item__overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: flex-start; justify-content: space-between; padding: 6px; opacity: 0; transition: opacity 0.2s; }
        .image-gallery-item:hover .image-gallery-item__overlay { opacity: 1; }
        .image-primary-badge { background: #2e7d32; color: #fff; font-size: 0.65rem; padding: 2px 6px; border-radius: 20px; font-weight: 600; display: flex; align-items: center; gap: 3px; }
        .image-delete-btn { width: 28px; height: 28px; border-radius: 50%; background: #ef4444; border: none; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; transition: background 0.2s; }
        .image-delete-btn:hover:not(:disabled) { background: #b91c1c; }
        .image-delete-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .image-gallery-item__num { position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.55); color: #fff; font-size: 0.65rem; padding: 1px 6px; border-radius: 10px; }
        @media (hover: none) { .image-gallery-item__overlay { opacity: 1; } }
      `}</style>
      <div className={`image-dropzone ${dragging ? 'image-dropzone--active' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}>
        <input ref={fileInputRef} type="file" multiple accept="image/*" style={{ display: 'none' }}
          onChange={e => doUpload(e.target.files)} />
        {uploading ? (
          <><div className="spinner" style={{ width: 28, height: 28, margin: '0 auto 10px' }} /><p style={{ color: '#2e7d32', fontWeight: 600 }}>Uploading...</p></>
        ) : (
          <><FiUpload size={28} style={{ marginBottom: 10, color: '#2e7d32' }} /><p style={{ fontWeight: 600, color: '#2e7d32', marginBottom: 4 }}>Drop images here or tap to upload</p><p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>PNG, JPG, WEBP — multiple allowed</p></>
        )}
      </div>
      {existingImages.length > 0 ? (
        <div className="image-gallery-grid">
          {existingImages.map((img, idx) => (
            <div key={img.id} className="image-gallery-item">
              <img src={img.image_url} alt={`Product ${idx + 1}`} />
              <div className="image-gallery-item__overlay">
                {img.is_primary && <span className="image-primary-badge"><FiStar size={9} />Primary</span>}
                <button className="image-delete-btn"
                  onClick={e => { e.stopPropagation(); handleDeleteImage(img.id); }}
                  disabled={deletingId === img.id} aria-label="Delete image">
                  {deletingId === img.id ? '…' : <FiTrash2 size={12} />}
                </button>
              </div>
              <div className="image-gallery-item__num">{idx + 1}</div>
            </div>
          ))}
        </div>
      ) : (
        !uploading && <p style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)', padding: '8px 0' }}>
          <FiImage size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />No images yet
        </p>
      )}
    </div>
  );
};

/* ─── Products Management ──────────────────────────────────── */
const ProductsManagement = () => {
  const [products, setProducts]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm]           = useState(false);
  const [saving, setSaving]               = useState(false);
  const [reordering, setReordering]       = useState(false);
  const [activeTab, setActiveTab]         = useState('basic');
  const [form, setForm] = useState({
    name: '', slug: '', short_description: '', description: '',
    benefits: '', ingredients: '', how_to_use: '',
    meta_title: '', meta_description: '', is_active: true
  });

  const fetchProducts = useCallback(() => {
    setLoading(true);
    productAPI.getAdminAll()
      .then(res => {
        const d = res.data;
        setProducts(Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []));
      })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const refreshEditingProduct = useCallback(async () => {
    if (!editingProduct) return;
    try {
      const res = await productAPI.getAdminAll();
      const d = res.data;
      const all = Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []);
      const updated = all.find(p => p.id === editingProduct.id);
      if (updated) setEditingProduct(updated);
      setProducts(all);
    } catch {}
  }, [editingProduct]);

  const moveProduct = async (index, direction) => {
    const newProducts = [...products];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newProducts.length) return;
    [newProducts[index], newProducts[targetIndex]] = [newProducts[targetIndex], newProducts[index]];
    const updated = newProducts.map((p, i) => ({ ...p, order: i + 1 }));
    setProducts(updated);
    setReordering(true);
    try {
      await productAPI.bulkUpdateOrder(updated.map(p => ({ id: p.id, order: p.order })));
      toast.success('Product order saved');
    } catch {
      toast.error('Failed to save order');
      fetchProducts();
    } finally { setReordering(false); }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name, slug: product.slug,
      short_description: product.short_description,
      description: product.description,
      benefits: Array.isArray(product.benefits) ? product.benefits.join('\n') : product.benefits || '',
      ingredients: product.ingredients || '',
      how_to_use: product.how_to_use || '',
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || '',
      is_active: product.is_active,
    });
    setActiveTab('basic');
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingProduct(null);
    setForm({ name: '', slug: '', short_description: '', description: '', benefits: '', ingredients: '', how_to_use: '', meta_title: '', meta_description: '', is_active: true });
    setActiveTab('basic');
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, benefits: form.benefits.split('\n').filter(b => b.trim()) };
    try {
      if (editingProduct) {
        await productAPI.update(editingProduct.id, payload);
        toast.success('Product updated!');
        fetchProducts();
      } else {
        const res = await productAPI.create(payload);
        const created = res.data;
        toast.success('Product created! Now upload images.');
        setEditingProduct(created);
        setActiveTab('images');
        fetchProducts();
      }
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.values(errors).flat().forEach(e => toast.error(e));
      else toast.error('Failed to save product');
    } finally { setSaving(false); }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await productAPI.delete(product.id);
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Failed to delete'); }
  };

  const MODAL_TABS = [
    { key: 'basic',   label: 'Basic Info', icon: <FiPackage size={13} /> },
    { key: 'details', label: 'Details',    icon: <FiLayers size={13} /> },
    { key: 'images',  label: 'Images',     icon: <FiImage size={13} /> },
    { key: 'seo',     label: 'SEO',        icon: <FiTag size={13} /> },
  ];

  return (
    <div className="admin-section">
      <style>{`
        .admin-modal--wide { max-width: 780px; width: 95vw; }
        .admin-modal-tabs { display: flex; border-bottom: 1.5px solid var(--gray-200,#e5e7eb); padding: 0 16px; gap: 0; background: var(--gray-50,#f9fafb); overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
        .admin-modal-tabs::-webkit-scrollbar { display: none; }
        .admin-modal-tab { display: flex; align-items: center; gap: 6px; padding: 11px 14px; font-size: 0.82rem; font-weight: 500; border: none; background: none; cursor: pointer; color: var(--text-secondary,#6b7280); border-bottom: 2.5px solid transparent; margin-bottom: -1.5px; transition: color 0.15s, border-color 0.15s; white-space: nowrap; flex-shrink: 0; }
        .admin-modal-tab:hover { color: #2e7d32; }
        .admin-modal-tab--active { color: #2e7d32; border-bottom-color: #2e7d32; font-weight: 600; }
        .admin-tab-content { display: flex; flex-direction: column; gap: 16px; padding: 4px 0; }
        .seo-preview { margin-top: 8px; }
        .admin-products-list-view { display: flex; flex-direction: column; gap: 10px; }
        .admin-product-list-row { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; gap: 12px; transition: box-shadow 0.15s; flex-wrap: nowrap; }
        .admin-product-list-row:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
        .admin-product-list-row__thumb { width: 52px; height: 52px; border-radius: 8px; object-fit: cover; background: #f0fdf4; flex-shrink: 0; }
        .admin-product-list-row__thumb-placeholder { width: 52px; height: 52px; border-radius: 8px; background: #e8f5e9; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
        .product-reorder-btns { display: flex; flex-direction: column; gap: 4px; flex-shrink: 0; }
        .product-reorder-btn { width: 28px; height: 28px; border-radius: 6px; border: 1px solid #e5e7eb; background: #f9fafb; color: #374151; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; }
        .product-reorder-btn:hover:not(:disabled) { background: #e8f5e9; border-color: #2e7d32; color: #2e7d32; }
        .product-reorder-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        @media (max-width: 600px) {
          .admin-product-list-row { flex-wrap: wrap; }
          .admin-product-list-row .row-actions { width: 100%; display: flex; gap: 8px; }
          .admin-product-list-row .row-actions .btn { flex: 1; justify-content: center; }
          .admin-product-list-row__info-block { flex: 1; min-width: 0; }
          .admin-modal--wide { width: 100%; border-radius: 16px 16px 0 0; }
        }
      `}</style>

      <div className="admin-section__header">
        <h1 className="admin-section__title">Products</h1>
        <button className="btn btn-primary" onClick={handleNew}><FiPlus size={16} /> Add Product</button>
      </div>

      {showForm && (
        <div className="admin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) { setShowForm(false); fetchProducts(); } }}>
          <div className="admin-modal admin-modal--wide" role="dialog" aria-modal="true">
            <div className="admin-modal__header">
              <h2>{editingProduct ? `Edit: ${editingProduct.name}` : 'New Product'}</h2>
              <button className="admin-close-btn" onClick={() => { setShowForm(false); fetchProducts(); }} aria-label="Close modal">
                <FiX size={18} />
              </button>
            </div>
            <div className="admin-modal-tabs" role="tablist">
              {MODAL_TABS.map(tab => (
                <button key={tab.key} type="button" role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`admin-modal-tab ${activeTab === tab.key ? 'admin-modal-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>
            <form onSubmit={handleSave} className="admin-modal__body">
              {activeTab === 'basic' && (
                <div className="admin-tab-content">
                  <div className="admin-form-grid">
                    <div className="form-group">
                      <label className="form-label">Product Name *</label>
                      <input className="form-input" value={form.name} onChange={e => {
                        const name = e.target.value;
                        setForm(f => ({ ...f, name, slug: name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }));
                      }} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Slug *</label>
                      <input className="form-input" value={form.slug}
                        onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Short Description * <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(max 500)</span></label>
                    <textarea className="form-input" rows={2} maxLength={500}
                      value={form.short_description}
                      onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))} required />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 3 }}>{form.short_description.length}/500</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Full Description *</label>
                    <textarea className="form-input" rows={6} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required />
                  </div>
                  <div className="form-group admin-checkbox">
                    <label>
                      <input type="checkbox" checked={form.is_active}
                        onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                      <span>Active (visible to customers)</span>
                    </label>
                  </div>
                </div>
              )}
              {activeTab === 'details' && (
                <div className="admin-tab-content">
                  <div className="form-group">
                    <label className="form-label">Benefits <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(one per line)</span></label>
                    <textarea className="form-input" rows={5} value={form.benefits}
                      onChange={e => setForm(f => ({ ...f, benefits: e.target.value }))}
                      placeholder={"Deep nourishment\nBalances doshas\n100% natural"} />
                    {form.benefits && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {form.benefits.split('\n').filter(b => b.trim()).map((b, i) => (
                          <span key={i} style={{ background: '#e8f5e9', color: '#2e7d32', fontSize: '0.75rem', padding: '2px 10px', borderRadius: 20, border: '1px solid #c8e6c9' }}>{b}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="admin-form-grid">
                    <div className="form-group">
                      <label className="form-label">Ingredients</label>
                      <textarea className="form-input" rows={4} value={form.ingredients}
                        onChange={e => setForm(f => ({ ...f, ingredients: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">How to Use</label>
                      <textarea className="form-input" rows={4} value={form.how_to_use}
                        onChange={e => setForm(f => ({ ...f, how_to_use: e.target.value }))}
                        placeholder={"Step 1: Apply to scalp\nStep 2: Massage gently"} />
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'images' && (
                <div className="admin-tab-content">
                  {editingProduct ? (
                    <>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                        <FiImage size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {editingProduct.images?.length || 0} image(s) — first uploaded becomes primary
                      </p>
                      <ImageUploadZone
                        productId={editingProduct.id}
                        existingImages={editingProduct.images || []}
                        onRefresh={refreshEditingProduct}
                      />
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                      <FiImage size={40} style={{ marginBottom: 12, opacity: 0.35 }} />
                      <p style={{ fontWeight: 600, marginBottom: 6 }}>Save Basic Info first</p>
                      <p style={{ fontSize: '0.85rem' }}>Create the product first, then come back here to upload images.</p>
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'seo' && (
                <div className="admin-tab-content">
                  <div className="form-group">
                    <label className="form-label">SEO Title</label>
                    <input className="form-input" value={form.meta_title}
                      onChange={e => setForm(f => ({ ...f, meta_title: e.target.value }))}
                      placeholder={form.name || 'e.g. Best Ayurvedic Hair Oil – Vedonica International'} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SEO Description</label>
                    <textarea className="form-input" rows={3} value={form.meta_description}
                      onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
                      placeholder="Brief description for Google..." maxLength={500} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 3 }}>{form.meta_description.length}/500</p>
                  </div>
                  {(form.meta_title || form.name) && (
                    <div className="seo-preview">
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Google Preview</p>
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px' }}>
                        <p style={{ color: '#1a0dab', fontSize: '1rem', fontWeight: 500, marginBottom: 2 }}>
                          {form.meta_title || form.name} – Vedonica
                        </p>
                        <p style={{ color: '#006621', fontSize: '0.78rem', marginBottom: 4 }}>
                          vedonica.com/product/{form.slug}
                        </p>
                        <p style={{ color: '#545454', fontSize: '0.85rem', lineHeight: 1.5 }}>
                          {form.meta_description || form.short_description || 'No description set.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="admin-modal__footer">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowForm(false); fetchProducts(); }}>
                  {editingProduct ? 'Done' : 'Cancel'}
                </button>
                {activeTab !== 'images' && (
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create & Continue →')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <div className="loading-container"><div className="spinner" /></div> : (
        <>
          {products.length > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#fffbeb', border: '1px solid #fde68a',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16,
              fontSize: '0.8rem', color: '#92400e', flexWrap: 'wrap',
            }}>
              <FiMove size={14} style={{ flexShrink: 0 }} />
              <span>Use the ↑ ↓ arrows to change the display order on the store.</span>
              {reordering && <span style={{ marginLeft: 'auto', color: '#2e7d32', fontWeight: 600 }}>Saving order...</span>}
            </div>
          )}

          <div className="admin-products-list-view">
            {products.map((product, index) => (
              <div key={product.id} className="admin-product-list-row">
                <div className="product-reorder-btns">
                  <button className="product-reorder-btn" title="Move up"
                    disabled={index === 0 || reordering}
                    onClick={() => moveProduct(index, 'up')} aria-label="Move product up">
                    <FiChevronUp size={14} />
                  </button>
                  <button className="product-reorder-btn" title="Move down"
                    disabled={index === products.length - 1 || reordering}
                    onClick={() => moveProduct(index, 'down')} aria-label="Move product down">
                    <FiChevronDown size={14} />
                  </button>
                </div>

                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: '#e8f5e9', color: '#2e7d32',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                }}>
                  {index + 1}
                </div>

                {product.images?.length > 0
                  ? <img className="admin-product-list-row__thumb" src={product.images[0].image_url} alt={product.name} />
                  : <div className="admin-product-list-row__thumb-placeholder">🌿</div>
                }

                <div className="admin-product-list-row__info-block" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <p style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{product.name}</p>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: product.is_active ? '#dcfce7' : '#fee2e2',
                      color: product.is_active ? '#16a34a' : '#dc2626',
                    }}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {product.short_description}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                    {product.variants?.map(v => (
                      <span key={v.id} className="admin-variant-tag">
                        Pack {v.pack_size}: ₹{Number(v.effective_price).toFixed(0)}
                        {v.discount_percentage > 0 && <span style={{ color: '#ef4444', marginLeft: 3 }}>({v.discount_percentage}% off)</span>}
                      </span>
                    ))}
                  </div>
                </div>

                {product.images?.length > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', flexShrink: 0, textAlign: 'center' }}>
                    <FiImage size={13} style={{ display: 'block', margin: '0 auto 2px' }} />
                    {product.images.length}
                  </div>
                )}

                <div className="row-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-ghost admin-action-btn" onClick={() => handleEdit(product)}>
                    <FiEdit2 size={14} /> Edit
                  </button>
                  <button className="btn admin-action-btn admin-action-btn--delete" onClick={() => handleDelete(product)}>
                    <FiTrash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}

            {products.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                <p style={{ fontSize: '2.5rem', marginBottom: 12 }}>🌿</p>
                <p style={{ fontWeight: 600, marginBottom: 12 }}>No products yet</p>
                <button className="btn btn-primary" onClick={handleNew}><FiPlus size={16} /> Add First Product</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Variants Management ──────────────────────────────────── */
const VariantsManagement = () => {
  const [products, setProducts]   = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variants, setVariants]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [variantForm, setVariantForm] = useState({ pack_size: 1, price: '', discounted_price: '', sku: '', stock: 100, is_active: true });
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    productAPI.getAdminAll().then(res => {
      const d = res.data;
      setProducts(Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []));
    }).catch(() => setProducts([]));
  }, []);

  const loadVariants = async (product) => {
    setSelectedProduct(product);
    setEditingVariant(null);
    setVariantForm({ pack_size: 1, price: '', discounted_price: '', sku: '', stock: 100, is_active: true });
    setLoading(true);
    try {
      const res = await productAPI.getVariants(product.id);
      const d = res.data;
      setVariants(Array.isArray(d) ? d : (Array.isArray(d.results) ? d.results : []));
    } catch { toast.error('Failed to load variants'); }
    finally { setLoading(false); }
  };

  const handleEditVariant = (v) => {
    setEditingVariant(v);
    setVariantForm({ pack_size: v.pack_size, price: v.price, discounted_price: v.discounted_price || '', sku: v.sku, stock: v.stock, is_active: v.is_active });
  };

  const handleSaveVariant = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...variantForm, product: selectedProduct.id, discounted_price: variantForm.discounted_price || null };
    try {
      if (editingVariant) {
        await productAPI.updateVariant(editingVariant.id, payload);
        toast.success('Variant updated!');
      } else {
        await productAPI.createVariant(payload);
        toast.success('Variant created!');
      }
      await loadVariants(selectedProduct);
      setEditingVariant(null);
      setVariantForm({ pack_size: 1, price: '', discounted_price: '', sku: '', stock: 100, is_active: true });
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.values(errors).flat().forEach(e => toast.error(e));
      else toast.error('Failed to save variant');
    } finally { setSaving(false); }
  };

  const discountPreview = variantForm.price && variantForm.discounted_price && Number(variantForm.price) > 0
    ? Math.round(((Number(variantForm.price) - Number(variantForm.discounted_price)) / Number(variantForm.price)) * 100)
    : 0;

  return (
    <div className="admin-section">
      <style>{`
        .variant-price-preview { display:flex; align-items:center; gap:10px; flex-wrap:wrap; background:#e8f5e9; border:1px solid #c8e6c9; border-radius:10px; padding:10px 14px; margin-bottom:8px; font-size:0.85rem; }
        .admin-table-row--editing td { background:#f0fdf4; }
      `}</style>
      <h1 className="admin-section__title">Variants & Pricing</h1>
      <div className="variants-product-select">
        <p className="admin-panel-label" style={{ marginBottom: 12 }}>Select Product</p>
        <div className="variants-product-pills">
          {products.map(p => (
            <button key={p.id}
              className={`variants-product-pill ${selectedProduct?.id === p.id ? 'variants-product-pill--active' : ''}`}
              onClick={() => loadVariants(p)}>
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {selectedProduct && (
        <div className="variants-layout">
          <div>
            <h2 className="admin-subtitle">
              <FiDollarSign size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {selectedProduct.name}
            </h2>
            {loading
              ? <div className="loading-container"><div className="spinner" /></div>
              : (
                <div className="variants-table-wrap admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Pack</th>
                        <th>MRP</th>
                        <th>Sale Price</th>
                        <th className="hide-xs">Discount</th>
                        <th className="hide-xs">SKU</th>
                        <th>Stock</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map(v => (
                        <tr key={v.id} className={editingVariant?.id === v.id ? 'admin-table-row--editing' : ''}>
                          <td><span className="admin-variant-tag">Pack {v.pack_size}</span></td>
                          <td style={{ textDecoration: v.discount_percentage > 0 ? 'line-through' : 'none', color: 'var(--text-secondary)' }}>₹{Number(v.price).toFixed(0)}</td>
                          <td style={{ fontWeight: 700, color: 'var(--green-primary)' }}>₹{Number(v.effective_price).toFixed(0)}</td>
                          <td className="hide-xs">{v.discount_percentage > 0 ? <span className="badge badge-green">{v.discount_percentage}% OFF</span> : <span style={{ color: 'var(--text-secondary)' }}>—</span>}</td>
                          <td className="hide-xs" style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{v.sku}</td>
                          <td>
                            <span style={{ fontWeight: 600, color: v.stock < 10 ? '#ef4444' : 'inherit' }}>{v.stock}</span>
                            {v.stock < 10 && <span style={{ fontSize: '0.68rem', color: '#ef4444', marginLeft: 3 }}>Low</span>}
                          </td>
                          <td><span className={`badge ${v.is_active ? 'badge-green' : 'badge-red'}`}>{v.is_active ? 'Active' : 'Off'}</span></td>
                          <td><button className="btn btn-ghost admin-action-btn" onClick={() => handleEditVariant(v)}><FiEdit2 size={13} /> Edit</button></td>
                        </tr>
                      ))}
                      {variants.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>No variants yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>

          <div className="variant-form-card card">
            <h3 className="admin-subtitle">{editingVariant ? `Editing Pack of ${editingVariant.pack_size}` : 'Add New Variant'}</h3>
            <form onSubmit={handleSaveVariant}>
              <div className="form-group">
                <label className="form-label">Pack Size</label>
                <select className="form-input form-select" value={variantForm.pack_size}
                  onChange={e => setVariantForm(f => ({ ...f, pack_size: Number(e.target.value) }))}>
                  <option value={1}>Pack of 1</option>
                  <option value={2}>Pack of 2</option>
                  <option value={3}>Pack of 3</option>
                </select>
              </div>
              <div className="admin-form-grid">
                <div className="form-group">
                  <label className="form-label">MRP / Original Price (₹) *</label>
                  <input type="number" className="form-input" value={variantForm.price}
                    onChange={e => setVariantForm(f => ({ ...f, price: e.target.value }))}
                    required min="0" step="0.01" placeholder="e.g. 999" />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Sale Price (₹)
                    {discountPreview > 0 && <span style={{ marginLeft: 8, background: '#e8f5e9', color: '#2e7d32', fontSize: '0.72rem', padding: '1px 8px', borderRadius: 20, fontWeight: 600 }}>{discountPreview}% OFF</span>}
                  </label>
                  <input type="number" className="form-input" value={variantForm.discounted_price}
                    onChange={e => setVariantForm(f => ({ ...f, discounted_price: e.target.value }))}
                    min="0" step="0.01" placeholder="Leave blank = no discount" />
                </div>
              </div>
              {discountPreview > 0 && (
                <div className="variant-price-preview">
                  <span>Customer pays:</span>
                  <span style={{ fontWeight: 700, color: 'var(--green-primary)' }}>₹{Number(variantForm.discounted_price).toFixed(0)}</span>
                  <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)' }}>₹{Number(variantForm.price).toFixed(0)}</span>
                  <span className="badge badge-green">{discountPreview}% OFF</span>
                </div>
              )}
              <div className="admin-form-grid">
                <div className="form-group">
                  <label className="form-label">SKU *</label>
                  <input className="form-input" value={variantForm.sku}
                    onChange={e => setVariantForm(f => ({ ...f, sku: e.target.value }))}
                    required placeholder="e.g. VED-OIL-P1" style={{ fontFamily: 'monospace' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Stock Quantity *</label>
                  <input type="number" className="form-input" value={variantForm.stock}
                    onChange={e => setVariantForm(f => ({ ...f, stock: Number(e.target.value) }))}
                    required min="0" />
                  {Number(variantForm.stock) < 10 && variantForm.stock !== '' && (
                    <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>⚠ Low stock warning</p>
                  )}
                </div>
              </div>
              <div className="form-group admin-checkbox">
                <label>
                  <input type="checkbox" checked={variantForm.is_active}
                    onChange={e => setVariantForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <span>Variant Active (shown to customers)</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {editingVariant && (
                  <button type="button" className="btn btn-ghost" onClick={() => {
                    setEditingVariant(null);
                    setVariantForm({ pack_size: 1, price: '', discounted_price: '', sku: '', stock: 100, is_active: true });
                  }}>Cancel</button>
                )}
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
                  {saving ? 'Saving...' : (editingVariant ? 'Update Variant' : 'Create Variant')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────── */
const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Close sidebar on section change
  const handleSetSection = (section) => {
    setActiveSection(section);
    setMobileSidebarOpen(false);
  };

  // Close sidebar on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && mobileSidebarOpen) setMobileSidebarOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileSidebarOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileSidebarOpen]);

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':       return <DashboardOverview />;
      case 'orders':          return <OrdersManagement />;
      case 'customer-orders': return <CustomerOrderHistory />;
      case 'products':        return <ProductsManagement />;
      case 'variants':        return <VariantsManagement />;
      case 'manual-order':    return <ManualOrderPlacement />;
      default:                return <DashboardOverview />;
    }
  };

  const sectionLabels = {
    dashboard: 'Dashboard', orders: 'Orders',
    'customer-orders': 'Customer History', products: 'Products',
    variants: 'Variants & Pricing', 'manual-order': 'Place Order',
  };

  return (
    <>
      <Helmet><title>Admin Panel – Vedonica International</title></Helmet>
      <div className="admin-layout">
        {/* Mobile toggle */}
        <button
          className="admin-mobile-toggle"
          onClick={() => setMobileSidebarOpen(o => !o)}
          aria-expanded={mobileSidebarOpen}
          aria-label="Toggle admin navigation">
          <FiMenu size={18} />
          <span>{sectionLabels[activeSection] || 'Admin Menu'}</span>
          <FiChevronDown size={14} style={{ marginLeft: 'auto', transform: mobileSidebarOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>

        {/* Sidebar overlay (mobile) */}
        {mobileSidebarOpen && (
          <div
            className="admin-sidebar-overlay"
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar drawer */}
        <div
          className={`admin-sidebar-wrap ${mobileSidebarOpen ? 'admin-sidebar-wrap--open' : ''}`}
          aria-hidden={!mobileSidebarOpen}>
          <Sidebar activeSection={activeSection} setActiveSection={handleSetSection} />
        </div>

        {/* Main content */}
        <main className="admin-main" id="admin-main-content">
          {renderSection()}
        </main>
      </div>
    </>
  );
};

export default AdminDashboard;